import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plus, Edit2, Trash2, Copy, X, Check, Pause, Play, BadgeDollarSign, Calendar, Users, Wand2 } from "lucide-react";
import ManualSubscriberModal from "@/components/console/ManualSubscriberModal";

const STATUS_LABELS = {
  active: "Active",
  paused: "Paused",
  full: "Full",
  waitlist_only: "Waitlist only",
  closed: "Closed",
};

const SUB_STATUS_LABELS = {
  active: "Active",
  paused: "Paused",
  cancelled: "Cancelled",
  completed: "Completed",
};

export default function ConsoleSubscriptions() {
  const [tab, setTab] = useState("plans"); // plans | seasons | subscriptions | reports
  return (
    <div data-testid="console-subscriptions">
      <div className="mb-8">
        <div className="bloom-divider mb-4 text-left">✦ Subscription Management ✦</div>
        <h1 className="font-serif text-5xl">Subscription engine</h1>
        <p className="font-serif italic text-[var(--bloom-ink-soft)] mt-2">
          Set the seasons, sculpt the plans, watch the lifecycle.
        </p>
      </div>

      <div className="flex items-center gap-2 mb-8 border-b border-[var(--bloom-border)]">
        {[
          { id: "plans", label: "Plans", icon: BadgeDollarSign },
          { id: "seasons", label: "Seasons", icon: Calendar },
          { id: "subscriptions", label: "Subscribers", icon: Users },
          { id: "reports", label: "Reports", icon: BadgeDollarSign },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-[11px] tracking-[0.18em] uppercase flex items-center gap-2 transition ${
                active
                  ? "border-b-2 border-[var(--bloom-ink)] text-[var(--bloom-ink)]"
                  : "border-b-2 border-transparent text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-ink)]"
              }`}
              data-testid={`subs-tab-${t.id === "subscriptions" ? "subscribers" : t.id}`}
            >
              <Icon size={14} strokeWidth={1.5} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "plans" && <PlansPanel />}
      {tab === "seasons" && <SeasonsPanel />}
      {tab === "subscriptions" && <SubscriptionsPanel />}
      {tab === "reports" && <ReportsPanel />}
    </div>
  );
}

/* ---------------- Plans ---------------- */
function PlansPanel() {
  const [plans, setPlans] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // plan | "new" | null
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    const [p, s] = await Promise.all([
      api.get("/admin/subscription-plans"),
      api.get("/admin/seasons"),
    ]);
    setPlans(p.data.plans || []);
    setSeasons(s.data.seasons || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onSave = async (payload, planId) => {
    setErr("");
    try {
      if (planId) {
        await api.patch(`/admin/subscription-plans/${planId}`, payload);
      } else {
        await api.post("/admin/subscription-plans", payload);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Could not save plan");
    }
  };

  const onDuplicate = async (id) => {
    await api.post(`/admin/subscription-plans/${id}/duplicate`);
    await load();
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this plan? Active subscriptions will block deletion.")) return;
    try {
      await api.delete(`/admin/subscription-plans/${id}`);
      await load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="text-[11px] tracking-[0.18em] uppercase text-[var(--bloom-ink-muted)]">
          {plans.length} plan{plans.length === 1 ? "" : "s"}
        </div>
        <button onClick={() => setEditing("new")} className="bloom-btn bloom-btn-primary" data-testid="add-plan">
          <Plus size={14} className="mr-2 inline" /> New plan
        </button>
      </div>

      {err && <ErrorBox err={err} />}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="plans-grid">
        {plans.map((p) => {
          const season = seasons.find((s) => s.id === p.season_id);
          return (
            <div key={p.id} className="bloom-card p-5" data-testid={`plan-${p.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-serif text-xl">{p.name}</div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--bloom-ink-muted)] mt-0.5">
                    {p.size} · {p.frequency}
                  </div>
                </div>
                <StatusPill status={p.status} />
              </div>
              <div className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mb-3 line-clamp-2">
                {p.description}
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm font-serif border-t border-[var(--bloom-border)] pt-3">
                <div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--bloom-ink-muted)]">Per delivery</div>
                  <div>${(p.per_delivery ?? p.price_nzd).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--bloom-ink-muted)]">Season total</div>
                  <div>${p.total_value.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--bloom-ink-muted)]">Subscribers</div>
                  <div>{p.current_subscribers}{p.max_subscribers ? ` / ${p.max_subscribers}` : ""}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--bloom-ink-muted)]">Season</div>
                  <div className="truncate">{season?.name || "·"}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <button onClick={() => setEditing(p)} className="bloom-btn bloom-btn-outline !px-4 !py-2 !text-[10px]" data-testid={`edit-plan-${p.id}`}>
                  <Edit2 size={12} className="mr-1 inline" /> Edit
                </button>
                <button onClick={() => onDuplicate(p.id)} className="bloom-btn bloom-btn-outline !px-4 !py-2 !text-[10px]" data-testid={`dupe-plan-${p.id}`}>
                  <Copy size={12} className="mr-1 inline" /> Duplicate
                </button>
                <button onClick={() => onDelete(p.id)} className="bloom-btn bloom-btn-outline !px-4 !py-2 !text-[10px]" data-testid={`del-plan-${p.id}`}>
                  <Trash2 size={12} className="mr-1 inline" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <PlanEditor
          plan={editing === "new" ? null : editing}
          seasons={seasons}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function PlanEditor({ plan, seasons, onClose, onSave }) {
  const [form, setForm] = useState({
    name: plan?.name || "",
    description: plan?.description || "",
    size: plan?.size || "small",
    frequency: plan?.frequency || "weekly",
    price_nzd: plan?.price_nzd || 65,
    season_id: plan?.season_id || (seasons[0]?.id || ""),
    status: plan?.status || "active",
    max_subscribers: plan?.max_subscribers ?? "",
    delivery_radius_km: plan?.delivery_radius_km ?? "",
    delivery_fee: plan?.delivery_fee ?? 0,
    pickup_option: !!plan?.pickup_option,
    courier_option: plan?.courier_option ?? true,
    free_delivery_threshold: plan?.free_delivery_threshold ?? "",
    sort_order: plan?.sort_order ?? 0,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price_nzd: Number(form.price_nzd),
      sort_order: Number(form.sort_order) || 0,
      delivery_fee: Number(form.delivery_fee) || 0,
      max_subscribers: form.max_subscribers === "" ? null : Number(form.max_subscribers),
      delivery_radius_km: form.delivery_radius_km === "" ? null : Number(form.delivery_radius_km),
      free_delivery_threshold: form.free_delivery_threshold === "" ? null : Number(form.free_delivery_threshold),
    };
    onSave(payload, plan?.id);
  };

  return (
    <div className="fixed inset-0 bg-[var(--bloom-ink)]/60 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[var(--bloom-bg)] border border-[var(--bloom-border)] max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 md:p-8" style={{ borderRadius: 6 }} data-testid="plan-editor">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl">{plan ? "Edit plan" : "New plan"}</h2>
          <button type="button" onClick={onClose} className="p-2"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name"><input className="bloom-input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="plan-name" /></Field>
          <Field label="Season">
            <select className="bloom-input" value={form.season_id} onChange={(e) => set("season_id", e.target.value)} data-testid="plan-season">
              {seasons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Size">
            <select className="bloom-input" value={form.size} onChange={(e) => set("size", e.target.value)} data-testid="plan-size-select">
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </Field>
          <Field label="Frequency">
            <select className="bloom-input" value={form.frequency} onChange={(e) => set("frequency", e.target.value)} data-testid="plan-frequency-select">
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="monthly">Monthly</option>
            </select>
          </Field>
          <Field label="Price per delivery (NZD)">
            <input type="number" min="0" step="0.01" className="bloom-input" value={form.price_nzd} onChange={(e) => set("price_nzd", e.target.value)} data-testid="plan-price" />
          </Field>
          <Field label="Status">
            <select className="bloom-input" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="plan-status">
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="Max subscribers">
            <input type="number" min="0" className="bloom-input" value={form.max_subscribers} onChange={(e) => set("max_subscribers", e.target.value)} placeholder="Unlimited" />
          </Field>
          <Field label="Sort order">
            <input type="number" className="bloom-input" value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} />
          </Field>
          <Field label="Delivery fee (NZD)">
            <input type="number" min="0" step="0.01" className="bloom-input" value={form.delivery_fee} onChange={(e) => set("delivery_fee", e.target.value)} />
          </Field>
          <Field label="Free delivery over (NZD)">
            <input type="number" min="0" step="0.01" className="bloom-input" value={form.free_delivery_threshold} onChange={(e) => set("free_delivery_threshold", e.target.value)} placeholder="Off" />
          </Field>
          <Field label="Delivery radius (km)">
            <input type="number" min="0" className="bloom-input" value={form.delivery_radius_km} onChange={(e) => set("delivery_radius_km", e.target.value)} />
          </Field>
          <div className="md:col-span-2">
            <Field label="Description">
              <textarea rows={3} className="bloom-input" style={{ resize: "vertical" }} value={form.description} onChange={(e) => set("description", e.target.value)} />
            </Field>
          </div>
          <div className="flex items-center gap-3">
            <input id="pickup_option" type="checkbox" checked={form.pickup_option} onChange={(e) => set("pickup_option", e.target.checked)} />
            <label htmlFor="pickup_option" className="font-serif text-sm">Pickup available</label>
          </div>
          <div className="flex items-center gap-3">
            <input id="courier_option" type="checkbox" checked={form.courier_option} onChange={(e) => set("courier_option", e.target.checked)} />
            <label htmlFor="courier_option" className="font-serif text-sm">Courier available</label>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 mt-6">
          <button type="button" onClick={onClose} className="bloom-btn bloom-btn-outline">Cancel</button>
          <button type="submit" className="bloom-btn bloom-btn-primary" data-testid="plan-save">Save</button>
        </div>
      </form>
    </div>
  );
}

/* ---------------- Seasons ---------------- */
function SeasonsPanel() {
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/seasons");
    setSeasons(data.seasons || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onSave = async (payload, seasonId) => {
    setErr("");
    try {
      if (seasonId) await api.patch(`/admin/seasons/${seasonId}`, payload);
      else await api.post("/admin/seasons", payload);
      setEditing(null);
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this season? Plans inside will block deletion.")) return;
    try {
      await api.delete(`/admin/seasons/${id}`);
      await load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="text-[11px] tracking-[0.18em] uppercase text-[var(--bloom-ink-muted)]">
          {seasons.length} season{seasons.length === 1 ? "" : "s"}
        </div>
        <button onClick={() => setEditing("new")} className="bloom-btn bloom-btn-primary" data-testid="add-season">
          <Plus size={14} className="mr-2 inline" /> New season
        </button>
      </div>

      {err && <ErrorBox err={err} />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {seasons.map((s) => (
          <div key={s.id} className="bloom-card p-5" data-testid={`season-${s.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-serif text-xl">{s.name}</div>
                <div className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mt-0.5">
                  {s.starts_on} → {s.ends_on}
                </div>
              </div>
              {s.is_current && (
                <span className="px-2.5 py-1 text-[9px] tracking-[0.2em] uppercase bg-[var(--bloom-ink)] text-[#FAF4FA]" style={{ borderRadius: 999 }}>Current</span>
              )}
            </div>
            {s.notes && <p className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mt-2">{s.notes}</p>}
            <div className="flex items-center gap-2 mt-4">
              <button onClick={() => setEditing(s)} className="bloom-btn bloom-btn-outline !px-4 !py-2 !text-[10px]">
                <Edit2 size={12} className="mr-1 inline" /> Edit
              </button>
              <button onClick={() => onDelete(s.id)} className="bloom-btn bloom-btn-outline !px-4 !py-2 !text-[10px]">
                <Trash2 size={12} className="mr-1 inline" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <SeasonEditor
          season={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function SeasonEditor({ season, onClose, onSave }) {
  const [form, setForm] = useState({
    name: season?.name || "Bloom Box Season",
    starts_on: season?.starts_on || "",
    ends_on: season?.ends_on || "",
    is_current: season?.is_current || false,
    notes: season?.notes || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    onSave(form, season?.id);
  };
  return (
    <div className="fixed inset-0 bg-[var(--bloom-ink)]/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[var(--bloom-bg)] border border-[var(--bloom-border)] max-w-lg w-full p-6 md:p-8" style={{ borderRadius: 6 }} data-testid="season-editor">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl">{season ? "Edit season" : "New season"}</h2>
          <button type="button" onClick={onClose} className="p-2"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Field label="Season name">
              <input className="bloom-input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="season-name" />
            </Field>
          </div>
          <Field label="Starts on">
            <input type="date" className="bloom-input" value={form.starts_on} onChange={(e) => set("starts_on", e.target.value)} data-testid="season-start" />
          </Field>
          <Field label="Ends on">
            <input type="date" className="bloom-input" value={form.ends_on} onChange={(e) => set("ends_on", e.target.value)} data-testid="season-end" />
          </Field>
          <div className="md:col-span-2 flex items-center gap-3">
            <input id="is_current" type="checkbox" checked={form.is_current} onChange={(e) => set("is_current", e.target.checked)} data-testid="season-current" />
            <label htmlFor="is_current" className="font-serif text-sm">Mark as the current season (shows on public subscribe page)</label>
          </div>
          <div className="md:col-span-2">
            <Field label="Notes">
              <textarea rows={2} className="bloom-input" style={{ resize: "vertical" }} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </Field>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 mt-6">
          <button type="button" onClick={onClose} className="bloom-btn bloom-btn-outline">Cancel</button>
          <button type="submit" className="bloom-btn bloom-btn-primary" data-testid="season-save">Save</button>
        </div>
      </form>
    </div>
  );
}

/* ---------------- Subscriptions table ---------------- */
function SubscriptionsPanel() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [showManual, setShowManual] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/admin/subscriptions${statusFilter ? `?status=${statusFilter}` : ""}`);
    setSubs(data.subscriptions || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, [statusFilter]);

  const setStatus = async (id, status) => {
    await api.post(`/admin/subscriptions/${id}/status`, { status });
    await load();
  };

  if (loading) return <Loading />;

  return (
    <div data-testid="subscriptions-panel">
      <div className="flex items-center gap-3 mb-6">
        <select className="bloom-input max-w-xs" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="subs-status-filter">
          <option value="">All statuses</option>
          {Object.entries(SUB_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={() => setShowManual(true)} className="bloom-btn bloom-btn-outline" data-testid="manual-subscriber-btn">
          <Wand2 size={14} /> Add subscriber manually
        </button>
        <div className="text-[11px] tracking-[0.18em] uppercase text-[var(--bloom-ink-muted)] ml-auto">
          {subs.length} subscription{subs.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="bloom-card overflow-x-auto">
        <table className="w-full text-sm font-serif">
          <thead className="border-b border-[var(--bloom-border)]">
            <tr className="text-left text-[10px] tracking-[0.18em] uppercase text-[var(--bloom-ink-muted)]">
              <th className="px-4 py-3">Subscriber</th>
              <th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3">Duration</th>
              <th className="px-4 py-3">Value</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {subs.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center font-serif italic text-[var(--bloom-ink-muted)]">No subscriptions yet.</td></tr>
            )}
            {subs.map((s) => (
              <tr key={s.id} className="border-b border-[var(--bloom-border)]/40" data-testid={`sub-${s.id}`}>
                <td className="px-4 py-3">
                  <div>{s.subscriber_name || "·"}</div>
                  <div className="text-[11px] text-[var(--bloom-ink-muted)] italic">{s.subscriber_email}</div>
                </td>
                <td className="px-4 py-3">{s.plan_name}</td>
                <td className="px-4 py-3 capitalize">{(s.duration_label || s.duration_mode || "").replace(/_/g, " ")}</td>
                <td className="px-4 py-3">${(s.total_value || 0).toFixed(2)}</td>
                <td className="px-4 py-3"><StatusPill status={s.status} sub /></td>
                <td className="px-4 py-3 text-right">
                  {s.status === "active" && (
                    <button onClick={() => setStatus(s.id, "paused")} className="text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-ink)] p-2" title="Pause"><Pause size={14} /></button>
                  )}
                  {s.status === "paused" && (
                    <button onClick={() => setStatus(s.id, "active")} className="text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-ink)] p-2" title="Resume"><Play size={14} /></button>
                  )}
                  {s.status !== "cancelled" && s.status !== "completed" && (
                    <button onClick={() => setStatus(s.id, "cancelled")} className="text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-accent)] p-2" title="Cancel"><X size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showManual && (
        <ManualSubscriberModal
          onClose={() => setShowManual(false)}
          onCreated={() => { load(); }}
        />
      )}
    </div>
  );
}

/* ---------------- Reports ---------------- */
function ReportsPanel() {
  const [report, setReport] = useState(null);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/admin/subscriptions/report");
      setReport(data);
    })();
  }, []);
  if (!report) return <Loading />;
  const { totals, revenue_by_size, revenue_by_frequency, projected_seasonal_revenue } = report;
  const fmt = (n) => `$${(n || 0).toFixed(2)}`;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="subs-reports">
      <KPI label="Active subscribers" value={totals.active} />
      <KPI label="Paused" value={totals.paused} />
      <KPI label="Cancelled" value={totals.cancelled} />
      <KPI label="Projected revenue" value={fmt(projected_seasonal_revenue)} large />
      <div className="bloom-card p-5 md:col-span-2">
        <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--bloom-ink-muted)] mb-3">Revenue by tier</div>
        {Object.entries(revenue_by_size).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between font-serif">
            <span className="capitalize">{k}</span>
            <span>{fmt(v)}</span>
          </div>
        ))}
      </div>
      <div className="bloom-card p-5 md:col-span-3">
        <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--bloom-ink-muted)] mb-3">Revenue by frequency</div>
        {Object.entries(revenue_by_frequency).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between font-serif">
            <span className="capitalize">{k}</span>
            <span>{fmt(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function KPI({ label, value, large }) {
  return (
    <div className="bloom-card p-5">
      <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--bloom-ink-muted)] mb-2">{label}</div>
      <div className={`font-serif ${large ? "text-3xl" : "text-2xl"}`}>{value}</div>
    </div>
  );
}

const STATUS_COLOURS = {
  active: "bg-[var(--bloom-ink)] text-[#FAF4FA]",
  paused: "bg-[var(--bloom-warning)]/20 text-[var(--bloom-warning)]",
  cancelled: "bg-[var(--bloom-accent)]/15 text-[var(--bloom-accent)]",
};
const STATUS_COLOUR_DEFAULT = "bg-[var(--bloom-surface)] text-[var(--bloom-ink-soft)]";

function StatusPill({ status, sub }) {
  const labels = sub ? SUB_STATUS_LABELS : STATUS_LABELS;
  const color = STATUS_COLOURS[status] || STATUS_COLOUR_DEFAULT;
  return (
    <span className={`inline-flex px-2.5 py-1 text-[9px] tracking-[0.2em] uppercase ${color}`} style={{ borderRadius: 999 }}>
      {labels[status] || status}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="bloom-label mb-2">{label}</div>
      {children}
    </div>
  );
}

function Loading() {
  return <div className="text-center font-serif italic text-[var(--bloom-ink-muted)] py-20">Loading…</div>;
}

function ErrorBox({ err }) {
  return (
    <div className="mb-4 p-3 border border-[var(--bloom-accent)] text-[var(--bloom-accent)] font-serif italic">
      {err}
    </div>
  );
}
